/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable react/prop-types */
import React, { ReactElement, useCallback, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Form, withFormik } from 'formik';
import { gql, useMutation } from '@apollo/client';
import { toast } from 'react-toastify';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';

import { Input, Dsgvo, CaptchaWrappper } from '../../PublicForm';
import { toastProps } from '../../../utils/configs';
import Patterns from '../../../shared/Patterns';

import '../../PublicForm/publicForm.scss';
import s from '../../PublicForm/publicForm.module.scss';

const SEND_INQUIRY_MUTATION = gql`
  mutation SendInquiry($input: InquiryInput!) {
    sendInquiry(input: $input) {
      success
    }
  }
`;

const ContactForm = (props) => {
  const { setFieldValue, errors, touched, values, defaultText, handleSubmit } =
    props;
  const submitEnabled = Object.keys(errors).length === 0 && values;

  const { executeRecaptcha } = useGoogleReCaptcha();

  useEffect(() => {
    setFieldValue('message', defaultText);
  }, [defaultText, setFieldValue]);

  const handleSubmitWithCaptcha = useCallback(async () => {
    if (!executeRecaptcha) {
      toast.error('Captcha fehlgeschlagen');
      return;
    }

    const captcha = await executeRecaptcha('contact');
    setFieldValue('captcha', captcha);
    handleSubmit();
  }, [executeRecaptcha, handleSubmit, setFieldValue]);

  return (
    <div className={s.formWrapper}>
      <Form
        onSubmit={(e) => {
          e.preventDefault();
          handleSubmitWithCaptcha();
        }}
      >
        <Input
          id="name"
          type="text"
          name="name"
          autoComplete="name"
          placeholder="Vor- und Nachname*"
          error={touched.name && errors.name}
        />
        <Input
          id="email"
          type="text"
          name="email"
          autoComplete="email"
          placeholder="E-mail*"
          error={touched.email && errors.email}
        />
        <Input
          id="telephone"
          type="text"
          name="telephone"
          autoComplete="tel"
          placeholder="Telefon*"
          error={touched.telephone && errors.telephone}
        />
        <Input
          id="message"
          type="textarea"
          as="textarea"
          name="message"
          placeholder="Anfrage"
          rows={4}
        />
        <Dsgvo error={touched.dsgvo && errors.dsgvo} />
        <input
          className={s.button}
          disabled={!submitEnabled}
          type="submit"
          value="Anfrage senden"
        />
      </Form>
    </div>
  );
};

ContactForm.propTypes = {
  setFieldValue: PropTypes.func.isRequired,
};

const ContactFormik = withFormik({
  mapPropsToValues: (props: any) => ({
    name: props.name || '',
    email: props.email || '',
    telephone: props.telephone || '',
    message: props.message || '',
    captcha: props.captcha || '',
  }),
  handleSubmit: async (values: any, { props }) => {
    const { customerId, projectId } = props;
    try {
      if (!values.dsgvo) throw new Error();
      const input = {
        ...values,
      };
      if (customerId && projectId) {
        input.customerId = customerId;
        input.projectId = projectId;
      }

      delete input.dsgvo;
      const response = await props.sendInquiry({
        variables: { input },
      });
      if (response && response.data?.sendInquiry?.success) {
        await toast.success('Anfrage erfolgreich gesendet!', toastProps);
      } else {
        await toast.error(
          'Anfrage konnte nicht abgesendet werden!',
          toastProps
        );
      }
    } catch (e) {
      await toast.error('Anfrage konnte nicht abgesendet werden!', toastProps);
    }
  },
  validate: (values) => {
    const errors: any = {};
    if (!values.email) {
      errors.email = 'Das Feld ist erforderlich.';
    } else if (!Patterns.email.test(values.email)) {
      errors.email = 'Keine korrekte Emailadresse';
    }
    if (!values.dsgvo) {
      errors.dsgvo =
        'Du musst die Bedingungen akzeptieren bevor du deine Mitteilung absendest.';
    }
    if (!values.name) {
      errors.name = 'Das Feld ist erforderlich.';
    }
    if (!values.telephone) {
      errors.telephone = 'Das Feld ist erforderlich.';
    }
    return errors;
  },
  validateOnChange: true,
})(ContactForm);

interface ContactFormWrapperProps {
  customerId?: number;
  projectId?: number;
  defaultText?: string;
}

const ContactFormWrapper = (props: ContactFormWrapperProps): ReactElement => {
  const { customerId, projectId, defaultText } = props;
  const [sendInquiry, { loading }] = useMutation(SEND_INQUIRY_MUTATION, {});
  return (
    <ContactFormik
      sendInquiry={sendInquiry}
      loading={loading}
      customerId={customerId}
      projectId={projectId}
      defaultText={defaultText}
    />
  );
};

ContactFormWrapper.defaultProps = {
  customerId: null,
  projectId: null,
  defaultText: '',
};

const ContactFormCaptchaWrapper = (
  props: ContactFormWrapperProps
): ReactElement => (
  <CaptchaWrappper>
    <ContactFormWrapper {...props} />
  </CaptchaWrappper>
);

ContactFormCaptchaWrapper.defaultProps = {
  customerId: null,
  projectId: null,
  defaultText: '',
};

export default ContactFormCaptchaWrapper;
